import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [sessionChecked, setSessionChecked] = useState(false)
  const [hasValidSession, setHasValidSession] = useState(false)

  useEffect(() => {
    let cancelled = false

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session && !cancelled) {
        setHasValidSession(true)
        setError('')
      }
    })

    const establish = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        const query = new URLSearchParams(window.location.search)
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        const type = hashParams.get('type') || query.get('type')
        const tokenHash = query.get('token_hash') || hashParams.get('token_hash')

        if (tokenHash) {
          const { data, error: otpError } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash: tokenHash,
          })
          if (cancelled) return
          if (otpError || !data.session) {
            setError('Invalid or expired reset link. Please request a new one.')
          } else {
            setHasValidSession(true)
          }
          return
        }

        if (accessToken) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          })
          if (cancelled) return
          if (sessionError || !data.session) {
            setError('Invalid or expired reset link. Please request a new one.')
          } else {
            setHasValidSession(true)
          }
          return
        }

        const { data: { session } } = await supabase.auth.getSession()
        if (cancelled) return
        if (session) {
          setHasValidSession(true)
        } else if (type === 'recovery') {
          setError('Invalid or expired reset link. Please request a new one.')
        } else {
          setError('No valid reset session found. Please use the link from your email.')
        }
      } catch (err) {
        console.error('[ResetPassword] Error:', err)
        if (!cancelled) setError('An error occurred. Please try again.')
      } finally {
        if (!cancelled) setSessionChecked(true)
      }
    }

    void establish()
    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      })

      if (updateError) {
        throw updateError
      }

      setSuccess(true)

      setTimeout(async () => {
        await supabase.auth.signOut()
        navigate('/login')
      }, 3000)
    } catch (err: unknown) {
      console.error('[ResetPassword] Update error:', err)
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  if (!sessionChecked) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚂</div>
          <p>Verifying reset link...</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: '1rem'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 24,
          padding: '3rem 2rem',
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #44c9ab, #3ab89a)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            fontSize: '2rem'
          }}>
            ✓
          </div>
          <h2 style={{ margin: '0 0 0.5rem', color: '#0f1720' }}>Password Updated!</h2>
          <p style={{ color: '#55616e', margin: 0 }}>
            Your password has been successfully changed. Redirecting to login...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '1rem'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 24,
        padding: '2.5rem 2rem',
        maxWidth: 400,
        width: '100%',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'linear-gradient(135deg, #efb776, #c5f5e4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '1.5rem'
          }}>
            🔐
          </div>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', color: '#0f1720' }}>
            Reset Password
          </h1>
          <p style={{ color: '#55616e', margin: 0, fontSize: '0.9rem' }}>
            Enter your new password below
          </p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(230, 110, 80, 0.1)',
            border: '1px solid rgba(230, 110, 80, 0.3)',
            borderRadius: 12,
            padding: '0.75rem 1rem',
            marginBottom: '1.5rem',
            color: '#c4433a',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {!hasValidSession ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#55616e', marginBottom: '1.5rem' }}>
              The reset link is invalid or has expired.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                width: '100%',
                padding: '0.875rem',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #44c9ab, #3ab89a)',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: '#0f1720',
                fontSize: '0.9rem'
              }}>
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: 12,
                  border: '1px solid rgba(16, 24, 32, 0.15)',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: '#0f1720',
                fontSize: '0.9rem'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: 12,
                  border: '1px solid rgba(16, 24, 32, 0.15)',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem',
                borderRadius: 12,
                border: 'none',
                background: loading
                  ? 'rgba(68, 201, 171, 0.5)'
                  : 'linear-gradient(135deg, #44c9ab, #3ab89a)',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#44c9ab',
              fontSize: '0.9rem',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  )
}
